import { useEffect, useRef, useState } from "react";

/** ---------------------- Tipos da API interna ---------------------- */
export type ColaboradorAPI = {
  id?: string | number;
  nome_completo: string;
  cpf?: string | null;
  setor?: string | null; // exibido como "Departamento"
  data_de_nascimento?: string | null; // ISO
  status_atual?: string | null; // status atual do colaborador (ex: "OK", "FALTA", "FOLGA")
};

type RespAPI = {
  ok?: boolean;
  meta?: any;
  data?: ColaboradorAPI[];
};

/** ---------------------- RHID: Constantes ---------------------- */

// Endpoint de apuração de ponto (RHiD)
const RHID_URL_APURACAO =
  "https://www.rhid.com.br/v2/report.svc/apuracao_ponto";

// Token JWT do RHID (use .env / variável de ambiente do Vite)
const RHID_TOKEN =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjaWRDdXN0b21lcklkIjoiNTI0ODMiLCJjaWRVc2VyVHlwZSI6IjEiLCJjaWRVc2VySWQiOiIxIiwiY2lkU2FsZXNDaGFubmVsSWQiOiIxNCIsImFjY2Vzc1R5cGUiOiIwIiwiY2lkQ3VzdG9tZXJEb21haW4iOiJnb29nbGVtYXJpbmUiLCJuYW1lIjoiQWRtaW5pc3RyYWRvciIsImVtYWlsIjoiYWRtQGdvb2dsZW1hcmluZS5jb20uYnIiLCJtYXhVc2VycyI6IjUwIiwibWF4VXNlcnNNb2JpbGUiOiI1MCIsIm1heENvbXBhbnkiOiIxIiwidXJsTG9nbyI6Imh0dHBzOi8vcmhpZHYyLnMzLXNhLWVhc3QtMS5hbWF6b25hd3MuY29tL2NvbnRyb2xpZC5wbmciLCJtb3N0cmFDaGF0IjoiRmFsc2UiLCJpZGlvbWEiOiJwdF9CUiIsIm1vc3RyYU5vdGlmaWNhY2FvRGFzaGJvYXJkIjoiVHJ1ZSIsImlzaURDbG91ZEN1c3RvbWVyIjoiRmFsc2UiLCJwZXJtaXRpckFwZW5hc0p1c3RpZmljYXRpdmFzIjoiRmFsc2UiLCJuYmYiOjE3NjM5OTI4MTYsImV4cCI6MTc2NDAwNzIxNiwiaWF0IjoxNzYzOTkyODE2LCJpc3MiOiJSSGlEIHYyLjAifQ.4qwOaNoUaQmx_JTDnBn_hffXpieb7sZDXyFFrdRwZp0";

// IDs das pessoas que você está consultando (mesmo array do curl)
const RHID_ID_PESSOAS = [
  28, 2, 41, 38, 3, 37, 5, 40, 32, 6, 39, 33, 7, 25, 31, 42, 9, 26, 43, 11, 12,
  13, 14, 16, 36, 1, 17, 34, 18, 19, 30, 23, 24,
];

/** ---------------------- Utils ---------------------- */
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Formata data => "YYYYMMDD" no padrão do RHID (ex: 20251124) */
function formatDateToRhid(d: Date): string {
  const year = d.getFullYear();
  const month = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  return `${year}${month}${day}`;
}

/** Converte minutos em "HH:MM" (só se você quiser usar depois) */
function minutosParaHHMM(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

/** Mapeia o retorno cru do RHID em nossos ColaboradorAPI */
function mapApuracaoToColaboradores(arr: any[]): ColaboradorAPI[] {
  return (arr || []).map((x) => {
    // status_atual a partir do alerta
    let status: string | null = null;

    if (x.colorAlert === "success") {
      status = "OK";
    } else if (x.colorAlert === "danger") {
      // normalmente "Falta", "Atraso", etc. em toolTipAlert
      status = x.toolTipAlert ?? "PROBLEMA";
    } else if (x.toolTipAlert) {
      status = x.toolTipAlert;
    }

    // Se quiser, poderia montar algo tipo "Falta (9h)" usando horasApenasFalta
    if (
      !status &&
      typeof x.horasApenasFalta === "number" &&
      x.horasApenasFalta > 0
    ) {
      status = `FALTA (${minutosParaHHMM(x.horasApenasFalta)})`;
    }

    return {
      id: x.idPerson ?? undefined,
      nome_completo: x.name ?? "",

      // Estes dados o RHID não está devolvendo aqui; deixo null
      cpf: null,
      setor: null,
      data_de_nascimento: null,

      status_atual: status,
    };
  });
}

/** ---------------------- Fetcher principal (RHID) ---------------------- */
export async function fetchColaboradoresOnce(): Promise<{
  data: ColaboradorAPI[];
  meta: RespAPI["meta"] | null;
}> {
  if (!RHID_TOKEN) {
    console.error(
      "[RHID] Token não definido. Configure VITE_RHID_TOKEN no .env do front ou use um backend proxy."
    );
    return { data: [], meta: null };
  }

  const hoje = new Date();
  const hojeStr = formatDateToRhid(hoje); // ex: "20251124"

  const body = {
    idPerson: RHID_ID_PESSOAS,
    ini: hojeStr,
    fim: hojeStr,
    afdChanges: [] as any[],
    pagina: 0,
  };

  const res = await fetch(RHID_URL_APURACAO, {
    method: "POST",
    headers: {
      "Content-Type": "application/json;charset=UTF-8",
      Authorization: `Bearer ${RHID_TOKEN}`,
      Accept: "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    throw new Error(`RHID HTTP ${res.status}`);
  }

  const json = await res.json(); // é um ARRAY de registros de apuração
  const arr = Array.isArray(json) ? json : [];

  const data = mapApuracaoToColaboradores(arr);
  return { data, meta: null };
}

/** ---------------------- Hook ---------------------- */
/**
 * Mantemos o nome `useEmpresasCliente` para não quebrar imports existentes.
 * Agora ele carrega a apuração do RHID para HOJE e mapeia para ColaboradorAPI.
 */
export function useEmpresasCliente() {
  const [data, setData] = useState<ColaboradorAPI[]>([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<RespAPI["meta"] | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const fetching = useRef(false);

  async function carregar() {
    fetching.current = true;
    try {
      setError(null);
      const r = await fetchColaboradoresOnce();
      setData(r.data);
      setMeta(r.meta);
    } catch (e: any) {
      console.error("Erro ao carregar apuração RHID:", e);
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      fetching.current = false;
      setLoading(false);
    }
  }

  useEffect(() => {
    carregar();
  }, []);

  const refresh = async () => {
    while (fetching.current) await sleep(25);
    setLoading(true);
    await carregar();
  };

  return { data, loading, refresh, meta, error };
}
